"use client";

import type { HeaderNavigation, HeaderSettings } from "@lib/types";
import Image from "next/image";
import Link from "next/link";
import { type ForwardedRef, forwardRef, useEffect, useRef, useState } from "react";

import { resolveCtaHref } from "@/components/shared/cta";
import { AnnouncementBanner } from "@/components/site/banners";
import { Container } from "@/components/ui/container";
import { icon, iconOrNull } from "@/components/ui/icons";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { SITE_NAME } from "@/constants";
import { urlForImageCached } from "@/sanity/lib/utils";

import { HeaderActionButton } from "./HeaderActions";
import { HeaderMenu, HeaderMobileSheetContent } from "./HeaderNavigation";
import { MobileAuthMenu } from "./MobileAuthMenu";

const MenuIcon = icon("Menu");
// const SearchIcon = icon("Search");
const CloseIcon = icon("ArrowUp");

type HeaderClientProps = {
  navigation: HeaderNavigation | null;
  settings: HeaderSettings | null;
  isDraftMode: boolean;
};

type HeaderBarProps = {
  logoUrl: string | null;
  logoAlt: string;
  isLogoSvg: boolean;
  isScrolled: boolean;
  desktopMenuOpen: boolean;
  setDesktopMenuOpen: (open: boolean) => void;
  mobileMenuOpen: boolean;
  setMobileMenuOpen: (open: boolean) => void;
  mobileAuthOpen: boolean;
  setMobileAuthOpen: (open: boolean) => void;
  hasPrimaryAction: boolean;
  hasSecondaryAction: boolean;
  primaryAction: HeaderSettings["primaryAction"] | null | undefined;
  secondaryAction: HeaderSettings["secondaryAction"] | null | undefined;
  navigation: HeaderNavigation | null;
};

const HeaderBar = forwardRef<HTMLDivElement, HeaderBarProps>(
  (
    {
      logoUrl,
      logoAlt,
      isLogoSvg,
      isScrolled,
      desktopMenuOpen,
      setDesktopMenuOpen,
      mobileMenuOpen,
      setMobileMenuOpen,
      mobileAuthOpen,
      setMobileAuthOpen,
      hasPrimaryAction,
      hasSecondaryAction,
      primaryAction,
      secondaryAction,
      navigation,
    },
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    return (
      <header
        ref={ref}
        className={`sticky inset-x-0 top-0 z-50 border-b border-neutral-800 transition-all duration-300 ${
          isScrolled ? "bg-neutral-900/55 backdrop-blur-md" : "bg-transparent"
        }`}
      >
        <Container className="py-padding-element-md">
          <div className="flex items-center justify-between">
            {/* Desktop Left - Menu & Logo */}
            <div className="desktop:flex z-[50] hidden items-center gap-6">
              <button
                onClick={() => setDesktopMenuOpen(!desktopMenuOpen)}
                className="text-foreground cursor-pointer transition-colors hover:text-neutral-300"
                aria-label="Menu"
              >
                {desktopMenuOpen ? (
                  <CloseIcon className="bg-emerald-darkest h-6 w-6 rounded-sm text-emerald-400" />
                ) : (
                  <MenuIcon className="h-6 w-6" />
                )}
              </button>

              {logoUrl ? (
                <Link href="/" onClick={() => setDesktopMenuOpen(false)}>
                  <Image
                    src={logoUrl}
                    alt={logoAlt}
                    width={183}
                    height={48}
                    className="h-[48px] w-auto"
                    priority
                    unoptimized={isLogoSvg}
                  />
                </Link>
              ) : null}
            </div>

            {/* Desktop Center - Search placeholder */}
            <div className="desktop:flex hidden flex-1 justify-end pr-6">
              {/* search removed for now */}
            </div>

            {/* Mobile - Logo */}
            {logoUrl ? (
              <Link href="/" className="relative z-[60]" onClick={() => setMobileMenuOpen(false)}>
                <Image
                  src={logoUrl}
                  alt={logoAlt}
                  width={183}
                  height={48}
                  className="desktop:hidden h-[48px] w-auto"
                  priority
                  unoptimized={isLogoSvg}
                />
              </Link>
            ) : null}

            {/* Desktop Right - Actions */}
            {hasPrimaryAction || hasSecondaryAction ? (
              <div className="desktop:flex z-[50] hidden items-center gap-4">
                <HeaderActionButton action={primaryAction} fallbackVariant="primary" />
                <HeaderActionButton action={secondaryAction} fallbackVariant="secondary" />
              </div>
            ) : null}

            {/* Mobile - Actions */}
            <div className="gap-gap-element-lg desktop:hidden flex items-center">
              {/* <button
                className="text-foreground transition-colors hover:text-neutral-300"
                aria-label="Search"
              >
                <SearchIcon className="h-5 w-5" />
              </button> */}

              <MobileAuthMenu
                primaryAction={primaryAction}
                secondaryAction={secondaryAction}
                isOpen={mobileAuthOpen}
                onOpenChange={setMobileAuthOpen}
              />

              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <button
                    className="text-foreground p-padding-element-3xs transition-colors hover:text-neutral-300"
                    aria-label="Menu"
                  >
                    <MenuIcon className="size-8" />
                  </button>
                </SheetTrigger>
                <SheetContent
                  side="right"
                  className="z-70 w-full border-0 bg-black p-0 [&>button]:hidden"
                  aria-describedby={undefined}
                >
                  <SheetTitle className="hidden" />
                  <HeaderMobileSheetContent
                    navigation={navigation}
                    primaryAction={primaryAction}
                    secondaryAction={secondaryAction}
                    onNavigate={() => setMobileMenuOpen(false)}
                    logoSrc={logoUrl}
                    logoAlt={logoAlt}
                    isLogoSvg={isLogoSvg}
                  />
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </Container>
      </header>
    );
  },
);

HeaderBar.displayName = "HeaderBar";

export default function HeaderClient({ navigation, settings }: HeaderClientProps) {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [desktopMenuOpen, setDesktopMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAuthOpen, setMobileAuthOpen] = useState(false);
  const [headerOffset, setHeaderOffset] = useState(0);

  const primaryAction = settings?.primaryAction;
  const secondaryAction = settings?.secondaryAction;
  const announcementBanner = settings?.announcementBanner;

  const hasPrimaryAction = Boolean(primaryAction?.label && resolveCtaHref(primaryAction?.href));
  const hasSecondaryAction = Boolean(
    secondaryAction?.label && resolveCtaHref(secondaryAction?.href),
  );

  const BannerIconCmp = iconOrNull(announcementBanner?.icon);
  const logoImage = settings?.logo;
  const logoUrl = logoImage ? urlForImageCached(logoImage)?.url() : null;
  const logoAlt = logoImage?.alt || SITE_NAME;
  const isLogoSvg = logoUrl?.toLowerCase().endsWith(".svg") ?? false;

  useEffect(() => {
    const measureHeader = () => {
      if (!headerRef.current) return;
      const rect = headerRef.current.getBoundingClientRect();
      // height is more stable for menu offset than `bottom` with sticky
      setHeaderOffset(rect.height);
    };

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      measureHeader();
    };

    const handleResize = () => {
      measureHeader();
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    handleScroll(); // initial

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <>
      {!desktopMenuOpen && (
        <AnnouncementBanner banner={announcementBanner} IconCmp={BannerIconCmp} />
      )}

      <HeaderBar
        ref={headerRef}
        logoUrl={logoUrl ?? null}
        logoAlt={logoAlt}
        isLogoSvg={isLogoSvg}
        isScrolled={isScrolled}
        desktopMenuOpen={desktopMenuOpen}
        setDesktopMenuOpen={setDesktopMenuOpen}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        mobileAuthOpen={mobileAuthOpen}
        setMobileAuthOpen={setMobileAuthOpen}
        hasPrimaryAction={hasPrimaryAction}
        hasSecondaryAction={hasSecondaryAction}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        navigation={navigation}
      />

      <HeaderMenu
        navigation={navigation}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
        open={desktopMenuOpen}
        onOpenChange={setDesktopMenuOpen}
        logoSrc={logoUrl}
        logoAlt={logoAlt}
        isLogoSvg={isLogoSvg}
        headerOffset={headerOffset}
      />
    </>
  );
}
